/**
 * The boundary where a report range of INSTANTS becomes business DAYS (ATL-1112).
 *
 * Kept apart from `recurringSpendingProration.js` on purpose: that module is the pure day
 * arithmetic and must stay free of imports so it can be tested without the atom graph. This
 * one is the thin edge that knows about stores and timezones, and it is the ONLY place a
 * `Date` is allowed to become a day.
 *
 * The screens hold their range as instants marking store-local day boundaries. Reading those
 * with device-local getters — which is what both P&L screens did — makes one store day
 * straddle two calendar months on any device outside the store's timezone, charging it twice.
 * Converting once, here, in the store's own frame removes the whole class.
 */

import { getStoreLocalDayString, normalizeDate } from '../../utils/timezoneUtils'
import { prorateMonthlyRecurringSpending } from './recurringSpendingProration'

/** The store id a value names, whether it is already an id or a store object. */
const storeIdOf = (store) => {
  if (typeof store === 'string') return store
  if (store && typeof store === 'object' && typeof store.id === 'string') return store.id
  // A legacy row whose `store` object carries no id must NOT be handed on as one: `storesDict`
  // would miss, the frame would silently fall back to the device clock, and this module exists
  // to remove exactly that dependency.
  return null
}

/**
 * What a recurring spending costs over an already-resolved report WINDOW.
 *
 * The window arrives as business days (`yyyy-MM-dd`) from `businessDateRangeStrings`, which is
 * the exact inverse of the encoding the report screens use. It is resolved ONCE for the whole
 * report rather than per spending, because inverting that encoding needs the set of stores the
 * range was built from — a single spending does not know it, and re-deriving the window from
 * its own store re-opens the multi-store autoclose defect one expense at a time.
 *
 * What IS per spending is its active window: `rS.date` / `rS.endDate` are read as plain
 * store-local CALENDAR days — see `calendarDay` below for why the autoclose-aware business-day
 * rule is wrong for them — in the frame of the spending's OWN store, which is not necessarily
 * the one selected on screen.
 *
 * @param {object} rS               The recurring spending. `rS.store` may be an id OR a store
 *                                  object; `amount`/`date`/`endDate` are the rest.
 * @param {object} window           `{ startDate, endDate }` as `yyyy-MM-dd` business days.
 * @param {?(string|{id?: string})} fallbackStoreId Frame to use when the spending names
 *                                  no store; also accepts either shape.
 */
export const recurringSpendingAmountForRange = (rS, window, fallbackStoreId = null) => {
  const frameStore = storeIdOf(rS?.store) || storeIdOf(fallbackStoreId)

  // `rS.date` / `rS.endDate` are CALENDAR DATES — the day the expense starts and stops being
  // charged — not instants inside a trading day, so they take the plain store-local calendar
  // day. Classifying them with the autoclose-aware business-day rule would move them one day
  // earlier at a store that trades past midnight: "until the 30th" would stop paying on the
  // 29th, and an expense created at 01:00 on a 04:00-cutoff store would start billing the day
  // before it was created. It is also what the detail screen renders back to the user.
  //
  // Both stored shapes want this treatment, which is why the rule is "calendar date" and not
  // "picked midnight": measured fleet-wide on live rows, `start_date` is a picked local
  // midnight in only 68 of 423 cases — `RecurringSpendingDetailScreen` defaults a new
  // spending's start to `new Date()`, so most carry a creation time of day, and it becomes a
  // midnight only if the user opens the Datepicker. `end_date` has no such default and so is
  // a picked midnight in 39 of the 52 rows that have one.
  const calendarDay = (value) => {
    const date = normalizeDate(value)
    return date ? getStoreLocalDayString(frameStore, date) : null
  }

  return prorateMonthlyRecurringSpending({
    amount: rS?.amount,
    startDay: window?.startDate ?? null,
    endDay: window?.endDate ?? null,
    activeFromDay: calendarDay(rS?.date),
    activeUntilDay: calendarDay(rS?.endDate),
  })
}
